import { useEffect } from 'react';

// Opt-in focus handling; existing native and MUI dialogs keep their own behavior.
export default function useDialogFocus() {
  useEffect(() => {
    let dialog = null;
    let opener = null;
    let lastOutsideFocus = document.activeElement;
    const openers = new WeakMap();
    let background = [];
    const rendered = element => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden' && !element.closest('[hidden]');
    const visible = element => rendered(element) && !element.closest('[inert]');
    const items = element => [...element.querySelectorAll('button:not(:disabled), input:not(:disabled):not([type="hidden"]), textarea:not(:disabled), select:not(:disabled), a[href], summary, [tabindex="0"]')].filter(visible);
    const layer = element => {
      let level = 0;
      for (let node = element; node && node !== document.body; node = node.parentElement) {
        const value = Number.parseInt(getComputedStyle(node).zIndex, 10);
        if (Number.isFinite(value)) level = Math.max(level, value);
      }
      return level;
    };
    const release = () => {
      const mobile = window.matchMedia('(max-width: 900px)').matches;
      const drawerOpen = document.querySelector('.iio-sidebar')?.classList.contains('is-open');
      background.forEach(([element, previous]) => {
        if (element.matches('.iio-sidebar')) element.inert = mobile && !drawerOpen;
        else if (element.matches('.iio-main, .iio-footer, .iio-topbar-search, .iio-topbar-right, .iio-topbar-brand')) element.inert = mobile && !!drawerOpen;
        else element.inert = previous;
      });
      background = [];
    };
    const remember = event => {
      if (!(event.target instanceof Element)) return;
      const owner = event.target.closest('[role="dialog"], [role="alertdialog"]');
      if (!owner || owner === dialog || (!dialog && !owner.matches('.iio-modal[data-focus-managed]'))) {
        const target = event.target.closest('button, a[href], [tabindex="0"], input');
        if (target) lastOutsideFocus = target;
      }
    };
    const update = () => {
      const top = [...document.querySelectorAll('[role="dialog"], [role="alertdialog"], .MuiPopover-root')]
        .filter(rendered).sort((a, b) => layer(a) - layer(b)).at(-1);
      const next = top?.matches('.iio-modal[data-focus-managed]') ? top : null;
      if (next === dialog || (!next && !dialog)) {
        // React may update its responsive inert attributes during a modal resize.
        if (dialog) background.forEach(([element]) => { if (!element.inert) element.inert = true; });
        return;
      }
      if (dialog) {
        release();
        if (!dialog.isConnected && opener?.isConnected && (!top || top.contains(opener))) opener.focus({ preventScroll: true });
      }
      dialog = next || null;
      if (!dialog) return;
      if (!openers.has(dialog)) openers.set(dialog, lastOutsideFocus);
      opener = openers.get(dialog);
      // Inert sibling branches, not the dialog's ancestor chain.
      let branch = dialog.closest('.iio-modal-overlay') || dialog;
      while (branch?.parentElement && branch.parentElement !== document.body) {
        [...branch.parentElement.children].filter(element => element !== branch && element instanceof HTMLElement).forEach(element => {
          background.push([element, element.inert]); element.inert = true;
        });
        branch = branch.parentElement;
      }
      if (!dialog.contains(document.activeElement)) {
        const field = [...dialog.querySelectorAll('input:not([type="hidden"]):not(:disabled), textarea:not(:disabled)')].find(visible);
        (field || items(dialog)[0])?.focus({ preventScroll: true });
      }
    };
    const keys = event => {
      if (!dialog) return;
      if (event.key === 'Escape') {
        const close = dialog.querySelector('.iio-modal-close:not(:disabled)');
        if (close) { event.preventDefault(); event.stopPropagation(); close.click(); }
      }
      if (event.key === 'Tab') {
        const controls = items(dialog), first = controls[0], last = controls.at(-1);
        if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) { event.preventDefault(); first?.focus(); }
      }
    };
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['inert', 'hidden', 'aria-hidden'] });
    document.addEventListener('pointerdown', remember, true);
    document.addEventListener('focusin', remember, true);
    document.addEventListener('keydown', keys, true);
    update();
    return () => {
      observer.disconnect(); release();
      document.removeEventListener('pointerdown', remember, true);
      document.removeEventListener('focusin', remember, true);
      document.removeEventListener('keydown', keys, true);
    };
  }, []);
}
